import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  debounce
} from 'lodash';
import {
  STAGE_CONFIRMATION,
  STAGE_SUBMISSION
} from '../../../dictionaries/Engagement.dic';
import Ui3DotsMenu from '../../../dictionaries/Ui3DotsMenu.dic';
import AccountLib from '../../../lib/Account';
import {
  Arr,
  join,
  sanitizeArr
} from '../../../lib/Array.lib';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import Core from '../../../lib/Core';
import {
  REC_MSG_TYPE__REJECTION_ID,
  STATE_ACTIVE
} from '../../../lib/Definition';
import fetchEmployerName from '../../../lib/Employer/fetchEmployerName.async';
import {
  EngagementLib
} from '../../../lib/Engagement';
import globalErrorHandler from '../../../lib/Error/globalErrorHandler.fun';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import fetchJobTitle from '../../../lib/Job/fetchJobTitle.async';
import jobSourceList from '../../../lib/jobSourceList';
import {
  Obj
} from '../../../lib/Object.lib';
import {
  COLLECTION__EMPLOYER_SOURCE_LISTS,
  COLLECTION__JOBS,
  readLoopbackRecord
} from '../../../lib/services/BE/loopback.api';
import {
  combineEmailsLists,
  mapEmailsListToRecipients,
  mapEmailsListToStringArray,
  sendSafeEmail
} from '../../../lib/services/Email/Email.lib';
import Streak from '../../../lib/Streak';
import {
  joinKeyName,
  trim
} from '../../../lib/String.lib';
import {
  getJobSourceByJobId
} from '../../../lib/tools/getJobSourceListApis';
import EmailPreview from '../../Dialogs/EmailPreview';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Checkbox from '../../Layout/Wrappers/Checkbox';
import DrawerBar from '../../Layout/Wrappers/DrawerBar';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import IconButton from '../../Layout/Wrappers/IconButton';
import Message from '../../Layout/Wrappers/Message';
import Once from '../../Layout/Wrappers/Once';
import ActionBarNotifyRecruiters from './ActionBarNotifyRecruiters';

const DATA__RULE__VALID_STAGES = [
  STAGE_SUBMISSION,
  STAGE_CONFIRMATION
];

export default function PreviewRecruiterJobUpdates({
  jobId,
  snippets,
  onlyNotify = false
}) {
  const [state, updateState] = useState();

  const DATA__COLLECTION = Arr(state.collection);
  const DATA__SELECTED = Arr(state.selected);
  const DATA__SOURCE_LIST = Arr(state.sourceList);
  const HASH__RECIPIENTS = Obj(state.recipients);

  const YES__ONLY_NOTIFY = YES(onlyNotify);
  const YES__DELETE_JOB_SOURCING = NOT(onlyNotify);
  const YES__COLLECTION = YES(state.collection);
  const YES__SELECTED = YES(state.selected);
  const YES__LENGTH__DATA__COLLECTION = YES(Arr(state.collection).length);
  const YES__LENGTH__DATA__SELECTED = YES(Arr(state.selected).length);
  const YES__CHECKED__ALL = DATA__SELECTED.length === DATA__COLLECTION.length;

  const NOT__COLLECTION = NOT(YES__COLLECTION);
  const NOT__LENGTH__DATA__COLLECTION = NOT(YES__LENGTH__DATA__COLLECTION);

  const _fetchData = async ({
    notifiedRecruiters = [],
  } = {}) => {

    Core.showLoader();

    const recruiterHash = {};
    const recipients = {};
    const sourceList = [];
    const sourceListIds = [];
    const recruiters = [];

    const __mapRecipients = ({ recruiter, candidate }) => {
      const recruiterRecipients = mapEmailsListToRecipients({
        emailsList: recruiter.emailsList,
        primaryContact: { name: recruiter._name, email: recruiter.email, accountType: 'Recruiter Main Contact' },
        accountType: 'Recruiter Additional Contact',
        messageTypes: [REC_MSG_TYPE__REJECTION_ID]
      });

      const candidateRecruiterRecipients = mapEmailsListToRecipients({
        emailsList: candidate ? candidate.emailsList : [],
        accountType: 'Candidate Recruiter Contact',
        messageTypes: [REC_MSG_TYPE__REJECTION_ID]
      });

      recruiterRecipients.emails = combineEmailsLists(candidateRecruiterRecipients.emails, recruiterRecipients.emails);
      recruiterRecipients.to = combineEmailsLists(candidateRecruiterRecipients.to, recruiterRecipients.to);
      recruiterRecipients.cc = combineEmailsLists(candidateRecruiterRecipients.cc, recruiterRecipients.cc);
      recruiterRecipients.bcc = combineEmailsLists(candidateRecruiterRecipients.bcc, recruiterRecipients.bcc);

      recipients[recruiter.id] = recruiterRecipients;
      recipients[recruiter.id]._to = mapEmailsListToStringArray(recipients[recruiter.id].to);
    }

    const employerId = trim(
      Obj(
        await readLoopbackRecord({
          collection: COLLECTION__JOBS,
          where: {
            id: jobId
          },
          fields: ['employerId'],
          limit: 1
        })
      ).employerId
    );

    fetchJobTitle({ jobId }).then(jobTitle => {
      fetchEmployerName({ employerId }).then(employerName => {
        updateState({
          employerName,
          jobTitle,
        });
      })
    });

    sourceList.push(...[

      // Get job source list
      ...Arr(await getJobSourceByJobId({ jobId })).map(
        (record) => ({
          ...record,
          YES__JOB_SOURCE_LIST: true
        })
      ),

      // Get employer source list
      ...Arr(
        await readLoopbackRecord({
          collection: COLLECTION__EMPLOYER_SOURCE_LISTS,
          where: {
            employerId,
            endDate: { eq: null }
          },
          mapper: (records) => records.map(
            record => ({
              ...record,
              YES__EMPLOYER_SOURCE_LIST: true,
            })
          )
        })
      )

    ]);

    sourceListIds.push(
      ...sanitizeArr(
        sourceList.map(
          ({ accountId }) => (accountId)
        )
      )
    );

    if (sourceListIds.length) {
      recruiters.push(...Arr(
        await AccountLib.getWhere({
          id: {
            inq: sourceListIds
          }
        })
      ));
    }

    recruiters.forEach(recruiter => {
      if (recruiter.state !== STATE_ACTIVE) { return; }
      recruiterHash[recruiter.id] = {
        id: recruiter.id,
        recruiter,
        YES__SOURCE_LIST: true
      };
      __mapRecipients({ recruiter });
    });

    // Get all recruiters with past submissions or  
    // who are in the confirmation or submission  
    // stage and have been matched.
    EngagementLib.cleanCache();
    Arr(
      await EngagementLib.getWhere({ jobId })
    ).filter(
      (
        engagement,
        index,
        array,
        YES__SUBMITTED = !!engagement.submitted,
        YES__VALID_STAGES = DATA__RULE__VALID_STAGES.includes(
          engagement.stage
        ),
        YES__MATCHED_BY_WHO = !!engagement.matchedByWho
      ) => (
        YES__SUBMITTED || (
          YES__VALID_STAGES &&
          YES__MATCHED_BY_WHO
        )
      )
    ).forEach(({
      recruiter = {},
      candidate = {},
      boxKey = null,
    }) => {
      if (!recruiterHash[recruiter.id]) {
        recruiterHash[recruiter.id] = {
          id: recruiter.id,
          recruiter,
          candidate,
          boxKey, YES__ENGAGEMENT: true
        };
      }
      recruiterHash[recruiter.id].DATA__CANDIDATE_NAMES = Arr(
        recruiterHash[recruiter.id].DATA__CANDIDATE_NAMES
      );
      recruiterHash[recruiter.id].DATA__CANDIDATE_NAMES.push(
        candidate._name
      );
      __mapRecipients({ recruiter, candidate });
    });

    const hashRecruitersToExclude = {};

    // Exclude recruiters that are already 
    // notified in past engagements (CloseJob)
    (() => {

      // Omit this block if only notifying
      if (YES__ONLY_NOTIFY) { return; }

      // Exclude recruiters that are already 
      // notified in active engagements (CloseJob)
      const sectionA = Obj(Core.getKeyValue('ActiveNotificationPreviewRef'));
      if (sectionA.jobId === jobId) {
        Arr(Fun(sectionA.getEngagements)()).forEach(
          ({ recruiter }) => (hashRecruitersToExclude[recruiter.id] = true)
        );
        Arr(Fun(sectionA.getClosedEngagements)()).forEach(
          ({ recruiter }) => (hashRecruitersToExclude[recruiter.id] = true)
        );
      }
      // Exclude recruiters that are already 
      // notified in pending engagements (CloseJob)
      const sectionB = Obj(Core.getKeyValue('PendingNotificationPreviewRef'));
      if (sectionB.jobId === jobId) {
        Arr(Fun(sectionB.getEngagements)()).forEach(
          ({ recruiter }) => (hashRecruitersToExclude[recruiter.id] = true)
        );
        Arr(Fun(sectionB.getClosedEngagements)()).forEach(
          ({ recruiter }) => (hashRecruitersToExclude[recruiter.id] = true)
        );
      }
    })();

    notifiedRecruiters.forEach(
      ({ recruiter }) => (hashRecruitersToExclude[recruiter.id] = true)
    );

    const collection = Object.keys(
      recruiterHash
    ).map(
      (key) => (recruiterHash[key])
    ).filter(
      ({ recruiter }) => NOT(
        hashRecruitersToExclude[recruiter.id]
      )
    );

    const selected = Arr(
      YES__LENGTH__DATA__SELECTED && collection.filter(
        ({ id }) => DATA__SELECTED.includes(id)
      ).map(
        ({ id }) => (id)
      )
    );

    await updateState({
      collection,
      sourceList,
      sourceListIds,
      recipients,
      selected,
      recruiters,
    });

    Core.hideMessage();

  }

  function _getSelectedItems() {
    return DATA__COLLECTION.filter(
      ({ id }) => DATA__SELECTED.includes(id)
    );
  }

  setControllerPreviewRecruiterJobUpdates({
    refetch: debounce(_fetchData),
    getSelectedItems: _getSelectedItems
  });

  const _onCheckAll = (value) => (event) => {
    if (YES__CHECKED__ALL) {
      updateState({ selected: [] });
    }
    else {
      const selected = DATA__COLLECTION.map(({ id }) => (id));
      updateState({ selected });
    }
  };

  const _onCheck = (value) => (event) => {
    const selected = [...DATA__SELECTED];
    const currentIndex = selected.indexOf(value);
    if (currentIndex === -1) {
      selected.push(value);
    }
    else {
      selected.splice(currentIndex, 1);
    }
    updateState({ selected });
  };

  const _onSubmit = ({
    status,
    rejectionReason,
    rejectionReasonAdditionalInfo,
    holdDate,
    holdDetails,
  }) => {
    try {
      const emailPreviewParams = getControllerEmailPreviewRecruiterJobUpdates().getParams();
      if (!emailPreviewParams.subject) {
        return Core.showError('Email subject is missing');
      }
      if (!emailPreviewParams.html) {
        return Core.showError('Email content is missing');
      }
      const selectedItems = _getSelectedItems();
      Core.dialog.open({
        title: <>Action Summary</>,
        paperStyle: {
          minWidth: '50vw',
          maxWidth: 'calc(100vw - 2rem)',
        },
        content: (
          <Fieldset
            title={Ui3DotsMenu.Job.previewRecruiterJobUpdates}
            wrapperProps={{
              className: 'mx-2'
            }}
          >
            <ul>
              {selectedItems.map((recruiter, index) => {
                const {
                  YES__SOURCE_LIST,
                  DATA__CANDIDATE_NAMES = []
                } = recruiter;
                return (
                  <li key={joinKeyName([
                    'recruiter_to_notify',
                    recruiter.id,
                    index
                  ])}>
                    <span>
                      {HASH__RECIPIENTS[recruiter.id]._to.join(', ')}
                    </span>
                    {
                      YES__DELETE_JOB_SOURCING &&
                      YES__SOURCE_LIST && (
                        <>
                          &nbsp;-&nbsp;
                          <span className='c-red'>To remove job from sourcing list</span>
                        </>
                      )
                    }
                    {!!DATA__CANDIDATE_NAMES.length &&
                      <>
                        &nbsp;-&nbsp;
                        <span className='c-black-medium f-sm'>
                          {join(DATA__CANDIDATE_NAMES)}
                        </span>
                      </>
                    }
                  </li>
                )
              })}
            </ul>
          </Fieldset>
        ),
        actions: [
          <Button outlined minW120>
            Cancel
          </Button>,
          <Button primary minW120
            onClick={async ev => {
              const hash = {};
              const recruiterJobSourcesToEdit = [];
              const uniqueNotifications = selectedItems.filter(({
                id,
                candidate = {},
                recruiter = {},
              }) => {
                if (!candidate._name) { recruiterJobSourcesToEdit.push(recruiter.id); }
                if (hash[id]) { return false; }
                hash[id] = true;
                return true;
              });
              Core.showLoader('Notifying recruiter');
              const ok = await Promise.allSettled(
                uniqueNotifications.map(async ({
                  id,
                  recruiter,
                  boxKey
                }, index) => {
                  const emailParams = {
                    ...emailPreviewParams,
                    to: join(HASH__RECIPIENTS[id]._to),
                    boxKey
                  };
                  await sendSafeEmail(emailParams);
                  if (
                    YES__DELETE_JOB_SOURCING &&
                    recruiterJobSourcesToEdit.includes(recruiter.id)
                  ) {
                    Core.showLoader('Editing Job Sourcing');
                    await Promise.allSettled(
                      DATA__SOURCE_LIST.map(async ({ id, accountId }) => {
                        if (recruiter.id === accountId) {
                          await jobSourceList.deleteJob(id);
                        }
                        return true;
                      })
                    );
                  }
                  return true;
                })
              ).catch(exception => Core.failure({ message: exception }));
              if (!ok) { return; }
              console.debug('NOTIFICATIONS', ok);
              setTimeout(() => {
                _fetchData({
                  notifiedRecruiters: uniqueNotifications,
                });
              });
            }}>
            Confirm
          </Button>
        ]
      });
    }
    catch (error) {
      globalErrorHandler(error);
    }
  }

  console.debug(
    'PreviewRecruiterJobUpdates',
    '\n', state
  );

  return (
    <Box column flex1>
      <Once
        condition={NOT__COLLECTION}
        promise={() => {
          return _fetchData();
        }}
      />
      <Message show={NOT__LENGTH__DATA__COLLECTION}>
        There are not recruiters with past submissions or actively sourcing for job
      </Message>
      <Box column
        acl={YES__LENGTH__DATA__COLLECTION}
      >
        {YES__COLLECTION && YES__SELECTED && (
          <Fieldset
            title="Notify recruiters with past submissions or actively sourcing for job"
            subtitle={
              <>
                {state.jobTitle} - {state.employerName}
              </>
            }
            nextToTitle={
              <IconButton
                icon='refresh'
                title="Refetch data"
                onClick={event => _fetchData()}
              />
            }
          >
            <Box column flex1
              style={{
                height: 'calc(100vh - 111px)',
              }}
            >
              <Box column flex1 className='scroll-y scroll-y-visible'
                style={{
                  minHeight: '90px',
                }}
              >
                <List sx={{ bgcolor: 'background.paper' }}>
                  <ListItem>
                    <ListItemButton dense onClick={_onCheckAll()}>
                      <ListItemIcon>
                        <Checkbox
                          checked={YES__CHECKED__ALL}
                          className='icon24'
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={`checkbox-list-label-all`}
                        primary={
                          <strong>
                            All ({DATA__COLLECTION.length || 0})
                          </strong>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {DATA__COLLECTION.map((
                    item,
                    index,
                    array,
                    YES__CHECKED = DATA__SELECTED.indexOf(item.id) !== -1
                  ) => {
                    const { id, recruiter, YES__SOURCE_LIST, DATA__CANDIDATE_NAMES = [] } = item;
                    const labelId = `checkbox-list-label-${id}`;
                    return (
                      <ListItem
                        key={id}
                        className='py-0'
                      >
                        <ListItemButton dense onClick={_onCheck(id)}>
                          <ListItemIcon>
                            <Checkbox
                              checked={YES__CHECKED}
                              className='icon24'
                            />
                          </ListItemIcon>
                          <ListItemText
                            className="truncate"
                            id={labelId}
                            primary={
                              <>
                                <span>{recruiter._name}</span>
                                {YES__SOURCE_LIST && (
                                  <>
                                    &nbsp;-&nbsp;
                                    <span className='c-red'>
                                      (currently sourcing for this role)
                                    </span>
                                  </>
                                )}
                                {!!DATA__CANDIDATE_NAMES.length &&
                                  <>
                                    &nbsp;-&nbsp;
                                    <span className='c-black-medium f-sm'>
                                      {join(DATA__CANDIDATE_NAMES)}
                                    </span>
                                  </>
                                }
                              </>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
              <Box column className='border-top'>
                <Box column className="px-2" style={{ maxHeight: '50vh' }}>
                  <EmailPreview taller omitUIRecipients
                    ref={setControllerEmailPreviewRecruiterJobUpdates}
                    snippets={snippets}
                  />
                </Box>
                <ActionBarNotifyRecruiters
                  selected={DATA__SELECTED}
                  noSubOptions
                  onSubmit={_onSubmit}
                />
              </Box>
            </Box>
          </Fieldset >
        )
        }
      </Box >
    </Box>
  );
}

const KEY__CONTROLLER__RECRUITER_JOB_UPDATES = 'RecruiterOtherNotificationPreviewRef';
const KEY__CONTROLLER__EMAIL_PREVIEW__RECRUITER_JOB_UPDATES = 'EmailPreviewRecruiterJobUpdates';

// CONTROLLER: ref setter
export function setControllerPreviewRecruiterJobUpdates(controller) {
  Core.setKeyValue(KEY__CONTROLLER__RECRUITER_JOB_UPDATES, controller);
}

// CONTROLLER: ref getter
export function getControllerPreviewRecruiterJobUpdates() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__RECRUITER_JOB_UPDATES));
}

// EMAIL PREVIEW: ref setter
export function setControllerEmailPreviewRecruiterJobUpdates(controller) {
  Core.setKeyValue(KEY__CONTROLLER__EMAIL_PREVIEW__RECRUITER_JOB_UPDATES, controller);
}

// EMAIL PREVIEW: ref getter
export function getControllerEmailPreviewRecruiterJobUpdates() {
  return Obj(Core.getKeyValue(KEY__CONTROLLER__EMAIL_PREVIEW__RECRUITER_JOB_UPDATES));
}

export async function openPreviewRecruiterJobUpdates({ jobId }) {
  Core.showLoader();
  const snippets = await Streak.getSnippets();
  Core.hideMessage();
  Core.openDrawer({
    style: {
      width: 1200,
      maxWidth: 'calc(100vw - 2rem)',
    },
    content: (
      <>
        <DrawerBar
          title={Ui3DotsMenu.Job.previewRecruiterJobUpdates}
        />
        <PreviewRecruiterJobUpdates onlyNotify
          jobId={jobId}
          snippets={snippets}
        />
      </>
    ),
  });
}
